/**
 * ContextFeatureBar — context-aware "key features" quick-access strip (Cabinet).
 *
 * Rendered JUST BELOW the cabinet context selector (TopBar) and ABOVE page
 * content. Surfaces the ~6-8 CORE features of the ACTIVE cabinet context as
 * icon+title shortcut chips so users aren't lost in the big left nav.
 *
 *   • DYNAMIC   — the item set swaps + re-runs its staggered entrance whenever
 *                 the active cabinet context changes (keyed on activeContext).
 *   • DESIGN    — modern icon+title chips, subtle 3D elevation, rounded, active
 *                 highlight, hover/press micro-animation, animated staggered
 *                 entrance, horizontally scrollable with fade edges on overflow,
 *                 theme + PER-CONTEXT accent aware (accent from GRC_CONTEXTS.color).
 *   • PURPOSE   — click → navigate + highlight the active chip.
 *   • A11Y      — <nav> landmark, aria-current, roving keyboard arrow nav,
 *                 respects prefers-reduced-motion.
 *
 * This is a SHORTCUT layer — it does NOT replace the sidebar / burger nav.
 * Featured items are curated in src/config/quick-nav.ts (route-verified) and
 * are aligned with the mobile cabinet feature sets (PRO_PERSONA_TABS).
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Link, useLocation } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { ChevronDown, ChevronUp, Sparkles } from 'lucide-react'

import { cn } from '@/lib/utils'
import { useContextStore } from '@/stores/context-store'
import { GRC_CONTEXTS, getContextMeta } from '@/config/contexts'
import { getQuickNavForContext } from '@/config/quick-nav'

const COLLAPSE_KEY = 'sgrc-adminclient-featurebar-collapsed'

/**
 * Per-context accent palette. The GRC_CONTEXTS `color` token is a Tailwind
 * `text-<hue>-500` class; we derive the hue and build self-contained class
 * fragments so each cabinet context carries its own hue on top of the theme.
 */
type Accent = {
  activeChip: string
  activeIcon: string
  idleIcon: string
  underline: string
  label: string
  badge: string
  ring: string
}

const ACCENTS: Record<string, Accent> = {
  amber: {
    activeChip: 'border-amber-500/45 bg-amber-500/10 text-amber-600 dark:text-amber-300 shadow-[0_2px_12px_-2px_rgba(217,119,6,0.45)]',
    activeIcon: 'from-amber-500 to-amber-600 text-white ring-amber-400/50',
    idleIcon: 'from-amber-500/15 to-amber-500/5 text-amber-600 dark:text-amber-300 ring-amber-500/15 group-hover:from-amber-500/25',
    underline: 'bg-amber-500/70', label: 'text-amber-600 dark:text-amber-300',
    badge: 'from-amber-500 to-amber-600', ring: 'focus-visible:ring-amber-500/60',
  },
  blue: {
    activeChip: 'border-blue-500/45 bg-blue-500/10 text-blue-600 dark:text-blue-300 shadow-[0_2px_12px_-2px_rgba(37,99,235,0.45)]',
    activeIcon: 'from-blue-500 to-blue-600 text-white ring-blue-400/50',
    idleIcon: 'from-blue-500/15 to-blue-500/5 text-blue-600 dark:text-blue-300 ring-blue-500/15 group-hover:from-blue-500/25',
    underline: 'bg-blue-500/70', label: 'text-blue-600 dark:text-blue-300',
    badge: 'from-blue-500 to-blue-600', ring: 'focus-visible:ring-blue-500/60',
  },
  teal: {
    activeChip: 'border-teal-500/45 bg-teal-500/10 text-teal-600 dark:text-teal-300 shadow-[0_2px_12px_-2px_rgba(13,148,136,0.45)]',
    activeIcon: 'from-teal-500 to-teal-600 text-white ring-teal-400/50',
    idleIcon: 'from-teal-500/15 to-teal-500/5 text-teal-600 dark:text-teal-300 ring-teal-500/15 group-hover:from-teal-500/25',
    underline: 'bg-teal-500/70', label: 'text-teal-600 dark:text-teal-300',
    badge: 'from-teal-500 to-teal-600', ring: 'focus-visible:ring-teal-500/60',
  },
  red: {
    activeChip: 'border-red-500/45 bg-red-500/10 text-red-600 dark:text-red-300 shadow-[0_2px_12px_-2px_rgba(220,38,38,0.45)]',
    activeIcon: 'from-red-500 to-red-600 text-white ring-red-400/50',
    idleIcon: 'from-red-500/15 to-red-500/5 text-red-600 dark:text-red-300 ring-red-500/15 group-hover:from-red-500/25',
    underline: 'bg-red-500/70', label: 'text-red-600 dark:text-red-300',
    badge: 'from-red-500 to-red-600', ring: 'focus-visible:ring-red-500/60',
  },
  purple: {
    activeChip: 'border-purple-500/45 bg-purple-500/10 text-purple-600 dark:text-purple-300 shadow-[0_2px_12px_-2px_rgba(147,51,234,0.45)]',
    activeIcon: 'from-purple-500 to-purple-600 text-white ring-purple-400/50',
    idleIcon: 'from-purple-500/15 to-purple-500/5 text-purple-600 dark:text-purple-300 ring-purple-500/15 group-hover:from-purple-500/25',
    underline: 'bg-purple-500/70', label: 'text-purple-600 dark:text-purple-300',
    badge: 'from-purple-500 to-purple-600', ring: 'focus-visible:ring-purple-500/60',
  },
  cyan: {
    activeChip: 'border-cyan-500/45 bg-cyan-500/10 text-cyan-600 dark:text-cyan-300 shadow-[0_2px_12px_-2px_rgba(8,145,178,0.45)]',
    activeIcon: 'from-cyan-500 to-cyan-600 text-white ring-cyan-400/50',
    idleIcon: 'from-cyan-500/15 to-cyan-500/5 text-cyan-600 dark:text-cyan-300 ring-cyan-500/15 group-hover:from-cyan-500/25',
    underline: 'bg-cyan-500/70', label: 'text-cyan-600 dark:text-cyan-300',
    badge: 'from-cyan-500 to-cyan-600', ring: 'focus-visible:ring-cyan-500/60',
  },
  indigo: {
    activeChip: 'border-indigo-500/45 bg-indigo-500/10 text-indigo-600 dark:text-indigo-300 shadow-[0_2px_12px_-2px_rgba(79,70,229,0.45)]',
    activeIcon: 'from-indigo-500 to-indigo-600 text-white ring-indigo-400/50',
    idleIcon: 'from-indigo-500/15 to-indigo-500/5 text-indigo-600 dark:text-indigo-300 ring-indigo-500/15 group-hover:from-indigo-500/25',
    underline: 'bg-indigo-500/70', label: 'text-indigo-600 dark:text-indigo-300',
    badge: 'from-indigo-500 to-indigo-600', ring: 'focus-visible:ring-indigo-500/60',
  },
  green: {
    activeChip: 'border-green-500/45 bg-green-500/10 text-green-600 dark:text-green-300 shadow-[0_2px_12px_-2px_rgba(22,163,74,0.45)]',
    activeIcon: 'from-green-500 to-green-600 text-white ring-green-400/50',
    idleIcon: 'from-green-500/15 to-green-500/5 text-green-600 dark:text-green-300 ring-green-500/15 group-hover:from-green-500/25',
    underline: 'bg-green-500/70', label: 'text-green-600 dark:text-green-300',
    badge: 'from-green-500 to-green-600', ring: 'focus-visible:ring-green-500/60',
  },
  slate: {
    activeChip: 'border-primary/45 bg-primary/10 text-primary shadow-[0_2px_12px_-2px_hsl(var(--primary)/0.45)]',
    activeIcon: 'from-primary to-primary/75 text-primary-foreground ring-primary/40',
    idleIcon: 'from-primary/15 to-primary/5 text-primary ring-primary/15 group-hover:from-primary/25',
    underline: 'bg-primary/60', label: 'text-primary',
    badge: 'from-primary to-primary/80', ring: 'focus-visible:ring-ring',
  },
}

const PRIMARY_ACCENT = ACCENTS.slate

/** Extract the hue token from a `text-<hue>-500` Tailwind class. */
function hueFromColorToken(token?: string): string {
  if (!token) return ''
  const m = token.match(/text-([a-z]+)-\d{2,3}/)
  return m ? m[1] : ''
}

const KEYFRAMES = `
@keyframes sgrc-fb-in {
  0%   { opacity: 0; transform: translateY(8px) scale(0.92); }
  55%  { opacity: 1; }
  100% { opacity: 1; transform: translateY(0) scale(1); }
}
@media (prefers-reduced-motion: reduce) {
  [data-testid="context-feature-bar"] a { animation: none !important; }
}
`

function readCollapsed(): boolean {
  try { return localStorage.getItem(COLLAPSE_KEY) === '1' } catch { return false }
}

export function ContextFeatureBar() {
  const { t, i18n } = useTranslation()
  const location = useLocation()
  const activeContext = useContextStore((s) => s.activeContext)
  const [collapsed, setCollapsed] = useState<boolean>(readCollapsed)

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const items = useMemo(() => getQuickNavForContext(activeContext), [activeContext, i18n.language])
  const meta = GRC_CONTEXTS[activeContext]
  const contextLabel = getContextMeta(activeContext).name
  const accent = ACCENTS[hueFromColorToken(meta?.color)] ?? PRIMARY_ACCENT

  // Longest-prefix match so exactly one chip is active at a time.
  const activeHref = useMemo(() => {
    let best = ''
    for (const it of items) {
      if (location.pathname === it.href || location.pathname.startsWith(`${it.href}/`)) {
        if (it.href.length > best.length) best = it.href
      }
    }
    return best
  }, [location.pathname, items])

  const toggle = useCallback(() => {
    setCollapsed((c) => {
      const next = !c
      try { localStorage.setItem(COLLAPSE_KEY, next ? '1' : '0') } catch { /* no-op */ }
      return next
    })
  }, [])

  // Edge-fade hints for the horizontal scroll row.
  const scrollRef = useRef<HTMLDivElement>(null)
  const [fade, setFade] = useState({ left: false, right: false })
  const updateFades = useCallback(() => {
    const el = scrollRef.current
    if (!el) return
    const left = el.scrollLeft > 4
    const right = el.scrollLeft + el.clientWidth < el.scrollWidth - 4
    setFade((f) => (f.left === left && f.right === right ? f : { left, right }))
  }, [])

  useEffect(() => {
    updateFades()
    const el = scrollRef.current
    if (!el) return
    const ro = new ResizeObserver(updateFades)
    ro.observe(el)
    window.addEventListener('resize', updateFades)
    return () => {
      ro.disconnect()
      window.removeEventListener('resize', updateFades)
    }
  }, [updateFades, items, collapsed])

  // Roving keyboard navigation across chips (Left/Right/Home/End).
  const onKeyDown = useCallback((e: React.KeyboardEvent<HTMLDivElement>) => {
    const dir = e.key === 'ArrowRight' ? 1 : e.key === 'ArrowLeft' ? -1 : 0
    const row = scrollRef.current
    if (!row) return
    const chips = Array.from(row.querySelectorAll<HTMLAnchorElement>('[data-fb-item]'))
    if (chips.length === 0) return
    const idx = chips.indexOf(document.activeElement as HTMLAnchorElement)
    if (dir !== 0) {
      e.preventDefault()
      const next = chips[Math.min(chips.length - 1, Math.max(0, (idx < 0 ? 0 : idx) + dir))]
      next?.focus()
    } else if (e.key === 'Home') {
      e.preventDefault(); chips[0]?.focus()
    } else if (e.key === 'End') {
      e.preventDefault(); chips[chips.length - 1]?.focus()
    }
  }, [])

  if (items.length === 0) return null

  return (
    <nav
      aria-label={t('shell.featureBarAria')}
      data-testid="context-feature-bar"
      data-collapsed={collapsed ? '1' : '0'}
      className={cn(
        'relative z-30 shrink-0 border-b border-border/60',
        'bg-gradient-to-b from-background via-background to-muted/40',
      )}
    >
      <style>{KEYFRAMES}</style>

      {collapsed ? (
        <div className="flex h-4 items-center justify-center">
          <button
            type="button"
            onClick={toggle}
            data-testid="feature-bar-toggle"
            aria-expanded={false}
            aria-label={t('shell.showFeatures')}
            className={cn(
              'flex h-3.5 items-center gap-1 rounded-full px-3 -translate-y-px',
              'text-muted-foreground/70 hover:text-primary hover:bg-primary/10 transition-colors',
              'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
            )}
          >
            <ChevronDown className="h-3 w-3" />
          </button>
        </div>
      ) : (
        <div className="flex h-11 items-center gap-1 pl-3 pr-1 sm:pl-4">
          <div className="mr-1 hidden shrink-0 select-none items-center gap-1.5 lg:flex">
            <Sparkles className={cn('h-3 w-3', accent.label)} />
            <span className="whitespace-nowrap text-[9px] font-bold uppercase tracking-[0.14em] text-muted-foreground">
              {t('shell.essentials')}{contextLabel ? ` · ${contextLabel}` : ''}
            </span>
          </div>

          <div className="relative min-w-0 flex-1">
            {fade.left && (
              <div aria-hidden className="pointer-events-none absolute inset-y-0 left-0 z-10 w-8 bg-gradient-to-r from-background to-transparent" />
            )}
            {fade.right && (
              <div aria-hidden className="pointer-events-none absolute inset-y-0 right-0 z-10 w-8 bg-gradient-to-l from-background to-transparent" />
            )}
            {/* A11Y: a <nav> full of <a> links needs no list/listitem roles — putting
                role="listitem" on an anchor (implicit role "link") is an invalid role
                override (axe: aria-allowed-role). The <nav aria-label> landmark +
                aria-current already convey structure. */}
            <div
              key={activeContext}
              ref={scrollRef}
              onScroll={updateFades}
              onKeyDown={onKeyDown}
              className={cn(
                'flex items-center gap-1.5 overflow-x-auto py-1 snap-x',
                '[scrollbar-width:none] [&::-webkit-scrollbar]:hidden',
              )}
            >
              {items.map((item, i) => {
                const active = item.href === activeHref
                const Icon = item.icon
                return (
                  <Link
                    key={item.href}
                    to={item.href}
                    data-fb-item=""
                    data-testid="feature-bar-item"
                    data-fb-href={item.href}
                    aria-current={active ? 'page' : undefined}
                    style={{
                      animation: 'sgrc-fb-in 440ms cubic-bezier(0.22, 1, 0.36, 1) both',
                      animationDelay: `${Math.min(i, 9) * 45}ms`,
                    }}
                    className={cn(
                      'group relative flex h-8 shrink-0 snap-start items-center gap-2 rounded-xl border pl-1 pr-2.5',
                      'select-none whitespace-nowrap text-[12px] font-semibold no-underline',
                      'transition-all duration-200 ease-out will-change-transform',
                      'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-1 focus-visible:ring-offset-background',
                      accent.ring,
                      active
                        ? accent.activeChip
                        : cn(
                            'border-border/60 bg-card text-muted-foreground',
                            'shadow-[0_1px_2px_rgba(16,24,40,0.05),0_3px_8px_-2px_rgba(16,24,40,0.08)]',
                            'dark:shadow-[0_1px_2px_rgba(0,0,0,0.35),0_6px_16px_-6px_rgba(0,0,0,0.5)]',
                            'hover:-translate-y-0.5 hover:border-border hover:text-foreground',
                            'hover:shadow-[0_3px_6px_rgba(16,24,40,0.08),0_10px_24px_-6px_rgba(16,24,40,0.16)]',
                            'dark:hover:shadow-[0_10px_28px_-8px_rgba(0,0,0,0.65)]',
                            'active:translate-y-0 active:scale-[0.97]',
                          ),
                    )}
                  >
                    <span
                      className={cn(
                        'flex h-6 w-6 items-center justify-center rounded-lg bg-gradient-to-br ring-1 transition-colors duration-200',
                        active ? cn(accent.activeIcon, 'shadow-sm') : accent.idleIcon,
                      )}
                    >
                      <Icon className="h-3.5 w-3.5" />
                    </span>
                    <span>{item.title}</span>
                    {item.badge && (
                      <span className={cn(
                        'ml-0.5 rounded-full bg-gradient-to-r px-1.5 py-px text-[9px] font-bold uppercase tracking-wide text-white shadow-sm',
                        accent.badge,
                      )}>
                        {item.badge}
                      </span>
                    )}
                    {active && (
                      <span
                        aria-hidden
                        className={cn('absolute inset-x-3 bottom-[3px] h-[2px] rounded-full', accent.underline)}
                      />
                    )}
                  </Link>
                )
              })}
            </div>
          </div>

          <button
            type="button"
            onClick={toggle}
            data-testid="feature-bar-toggle"
            aria-expanded
            aria-label={t('shell.hideFeatures')}
            className={cn(
              'flex h-7 w-7 shrink-0 items-center justify-center rounded-full',
              'text-muted-foreground/70 hover:bg-accent hover:text-foreground transition-colors',
              'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
            )}
          >
            <ChevronUp className="h-3.5 w-3.5" />
          </button>
        </div>
      )}
    </nav>
  )
}
